/**
 * Owner-register / manual-transaction / void API (TMMAPP-0008 / TMMAPP-0011, design §8.6) —
 * wire types from `src/api/generated/`; normalized into the module's domain type. `amountMinor`
 * is bank-side in `currency`; allocations and `remainderMinor` are in the operating currency
 * (MM-D-63). Pages are cursor-based: `{ items, page: { nextCursor? } }`.
 */
import { request } from './client';
import { personNameOrNull } from './personNames';
import { ApiShapeError } from './errors';
import { normalizeNamingSource, type NamingSource } from './namingSource';
import type {
  AllocationLine as WireAllocationLine,
  ManualTransactionRequest as WireManualTransactionRequest,
  OwnerRegisterLine as WireOwnerRegisterLine,
  OwnerRegisterPage as WireOwnerRegisterPage,
  OwnerWideRegisterPage as WireOwnerWideRegisterPage,
  PlannedOccurrenceRef as WirePlannedOccurrenceRef,
  RegisterNamedTransfer as WireRegisterNamedTransfer,
  RegisterTotals as WireRegisterTotals,
  RegisterWire as WireRegisterWire,
  RegisterZelle as WireRegisterZelle,
  VoidRequest,
} from './generated';

export type TransactionSource = 'BANK_FILE' | 'MANUAL' | 'AUTO_POST' | 'CONNECTOR' | 'ADJUSTMENT';

/** An allocation's origin, the contract's `AllocationOrigin` enum (TMMAPI-0157). */
export type AllocationOrigin = NonNullable<WireAllocationLine['origin']>;

const ALLOCATION_ORIGINS: ReadonlySet<string> = new Set<AllocationOrigin>([
  'USER',
  'RULE',
  'PLAN',
  'AI',
  'SPLIT',
]);

/** The served origin, or null when absent or a value this build doesn't know. */
export function normalizeAllocationOrigin(
  value: string | null | undefined,
): AllocationOrigin | null {
  return value != null && ALLOCATION_ORIGINS.has(value) ? (value as AllocationOrigin) : null;
}
export type TransactionStatus = 'POSTED' | 'VOID';
/** How a line got its payee (TMM-B9): by hand, by an alias, or by an accepted AI name. */
export type PayeeSource = 'MANUAL' | 'ALIAS' | 'AI';

const PAYEE_SOURCES: ReadonlySet<string> = new Set(['MANUAL', 'ALIAS', 'AI']);

/** The served `payeeSource`, or null when absent or unknown — never inferred. */
export function normalizePayeeSource(value: string | null | undefined): PayeeSource | null {
  return value != null && PAYEE_SOURCES.has(value) ? (value as PayeeSource) : null;
}

/** Resolved counterparty beside the bank text (MM-D-19). */
export interface PayeeRef {
  id: number;
  name: string;
}
/** MM-D-128: a part whose category kind runs against the money's direction, computed server-side. */
export type AllocationContra = 'REFUND' | 'GIVE_BACK';

export interface AllocationLine {
  id: number;
  /** The envelope part's envelope; null on an Unassigned part (TMMAPI-0145). */
  envelopeId: number | null;
  envelopeName: string | null;
  /** The part's virtual account (served on Unassigned parts; null when not served). */
  virtualAccountId: number | null;
  /** True on a part that sits in the virtual account's Unassigned (MM-D-112). */
  unassigned: boolean;
  virtualAccountLabel: string | null;
  envelopeOwnerDisplayName: string | null;
  amountMinor: number;
  categoryId: number | null;
  /** "Refund" / "Give-back" marker (MM-D-128); null otherwise. */
  contra: AllocationContra | null;
  /** Who wrote the part (TMMAPI-0157: the generated enum); null when absent or unknown. */
  origin: AllocationOrigin | null;
  actor: string | null;
  meetingOccurrenceId: number | null;
  ruleId: number | null;
  ruleName: string | null;
}

export interface PlannedOccurrenceRef {
  seriesId: number;
  originalOn: string;
  payee: string;
  amountMinor: number;
  state: string;
}

export interface OwnerRegisterLine {
  id: number;
  postedOn: string;
  amountMinor: number;
  currency: string;
  operatingAmountMinor: number;
  fxRate: number | null;
  payee: string;
  /** Resolved counterparty beside the bank text (MM-D-19); null when unnamed. */
  payeeRef: PayeeRef | null;
  memo: string | null;
  runningBalanceMinor: number | null;
  source: TransactionSource;
  status: TransactionStatus;
  allocations: AllocationLine[];
  remainderMinor: number;
  /** Operating amount already allocated. */
  allocatedMinor: number;
  /** Operating amount after allocations; null when the wire omits it. */
  afterAllocationsMinor: number | null;
  plannedOccurrence: PlannedOccurrenceRef | null;
  /** Set when the line is classified as a transfer (TMMAPP-0026). */
  transferAccountId: number | null;
  transferAccountName: string | null;
  transferPairTransactionId: number | null;
  /** Counterparty operating amount when paired (YAML `counterpartAmountMinor`). */
  counterpartAmountMinor: number | null;
  /** The import batch the line came in with (TMMAPI-0143, owner); null when none or not served. */
  importBatchId: number | null;
  /**
   * What everyone but the owner reads in place of the payee (MM-D-117): the line's own name, else
   * its payee's default; null when neither is set (TMMAPI-0147).
   */
  alternateName: string | null;
  /**
   * Set only when the line is dated on or before the opening date. The wire omits it otherwise;
   * a false value is not stored.
   */
  beforeOpening?: true;
  /** How the line got its payee (TMM-B9: `AI` keeps the AI mark); absent when not served. */
  payeeSource?: PayeeSource;
  /**
   * How the line's payee name was made (TMMAPI-0193 U3): the person's rule, a known merchant, the
   * bank's text or AI. Absent while unnamed, and when unknown.
   */
  namingSource?: NamingSource;
  /** Set only when a person confirmed the line's payee name (TMMAPI-0193 U3). */
  confirmedByPerson?: true;
  /** Set only when the line is a wire (TMMAPI-0165, owner). Absent otherwise. */
  wire?: RegisterWire;
  /** Set only when the line is a Zelle payment (TMMAPI-0170, owner). Absent otherwise. */
  zelle?: RegisterZelle;
  /** Set only when the line is a same-bank transfer naming someone (TMMAPI-0175, owner). */
  transfer?: RegisterNamedTransfer;
}

/**
 * A same-bank transfer that names a counterparty (TMMAPI-0175): BofA's "Mobile transfer … ; NAME",
 * Wells Fargo's named transfers, a credit union's member-to-member line. Owner-only.
 */
export interface RegisterNamedTransfer {
  direction: string;
  /** The other account's last digits, when the bank prints them. */
  mask: string | null;
  counterpartyName: string | null;
  namesOwner: boolean;
  /**
   * The line's `XFER:` identity key: the key `claim-transfer-lines {identityKey}` uses. Null when
   * the transfer prints no name or the key is too long to store. Never parsed.
   */
  identityKey: string | null;
}

/** A Zelle payment on an owner register line (TMMAPI-0170). */
export interface RegisterZelle {
  direction: string;
  counterpartyName: string | null;
  namesOwner: boolean;
  /**
   * The line's counterparty identity key (`ZELLE:…`, TMMAPI-0172): the key its `MISSING_ACCOUNT`
   * item and `claim-transfer-lines {identityKey}` use. Null when the line names nobody.
   */
  identityKey: string | null;
}

/** The line's counterparty on any rail (TMMAPP-0136 item 8; named transfers, TMMAPP-0138 item 7). */
export type LineCounterparty =
  | { rail: 'WIRE'; wire: RegisterWire }
  | { rail: 'ZELLE'; zelle: RegisterZelle }
  | { rail: 'TRANSFER'; transfer: RegisterNamedTransfer };

/** The line's wire, Zelle or named-transfer block; null when it has none. */
export function lineCounterparty(line: {
  wire?: RegisterWire | undefined;
  zelle?: RegisterZelle | undefined;
  transfer?: RegisterNamedTransfer | undefined;
}): LineCounterparty | null {
  if (line.wire !== undefined) return { rail: 'WIRE', wire: line.wire };
  if (line.zelle !== undefined) return { rail: 'ZELLE', zelle: line.zelle };
  if (line.transfer !== undefined) return { rail: 'TRANSFER', transfer: line.transfer };
  return null;
}

/** A wire on an owner register line (TMMAPI-0165). Owner only; absent when the line isn't one. */
export interface RegisterWire {
  direction: string;
  counterpartyName: string | null;
  identifierKind: string | null;
  identifier: string | null;
  identifierDisplay: string | null;
  bank: string | null;
  namesOwner: boolean;
  /**
   * The line's counterparty identity key (`ACCT:…` or `NAME:…`, TMMAPI-0172): the key its
   * `MISSING_ACCOUNT` item and `claim-transfer-lines {identityKey}` use. Null when the line names
   * nobody or the wire is unparsed.
   */
  identityKey: string | null;
}

export interface OwnerRegisterQuery {
  from?: string | undefined;
  to?: string | undefined;
  unallocatedOnly?: boolean | undefined;
  /** Mutually exclusive with `unallocatedOnly`. Not wired to a control. */
  allocatedOnly?: boolean | undefined;
  cursor?: string | undefined;
  limit?: number | undefined;
  /** Text, payee, memo, envelope, category or an exact amount (TMMAPI-0165). 2–100 characters. */
  q?: string | undefined;
}

export interface OwnerRegisterTotals {
  allocatedMinor: number;
  count: number;
  currency: string;
  inMinor: number;
  outMinor: number;
  unallocatedMinor: number;
  /** The window's line count ignoring the allocation filters (TMMAPI-0143). */
  allCount: number;
  /** After allocations at the window's end (TMMAPI-0143); null when not served (LOAN omits it). */
  afterAllocationsMinor: number | null;
  /** The window's net (signed; tmm-api 0145) in the account's currency. */
  netMinor: number;
}

export interface OwnerRegisterPageResult {
  items: OwnerRegisterLine[];
  nextCursor: string | null;
  totals: OwnerRegisterTotals;
}

export interface ManualTransactionRequest {
  postedOn: string;
  amountMinor: number;
  payee: string;
  memo?: string;
}

const SOURCES: ReadonlySet<string> = new Set([
  'BANK_FILE',
  'MANUAL',
  'AUTO_POST',
  'CONNECTOR',
  'ADJUSTMENT',
]);

const CONTRAS: ReadonlySet<string> = new Set(['REFUND', 'GIVE_BACK']);

/** The served `contra` marker; anything else (absent, unknown) is none. */
export function normalizeContra(value: string | null | undefined): AllocationContra | null {
  return value != null && CONTRAS.has(value) ? (value as AllocationContra) : null;
}

function normalizeAllocationLine(w: WireAllocationLine): AllocationLine {
  // A part targets an envelope or a virtual account's Unassigned (TMMAPI-0145); one or the other.
  const unassigned = w.unassigned === true;
  return {
    id: w.id,
    envelopeId: unassigned ? null : (w.envelopeId ?? null),
    envelopeName: w.envelopeName ?? null,
    virtualAccountId: w.virtualAccountId ?? null,
    unassigned,
    virtualAccountLabel: w.virtualAccountLabel ?? null,
    envelopeOwnerDisplayName: personNameOrNull(w.envelopeOwnerDisplayName),
    amountMinor: w.amountMinor,
    categoryId: w.categoryId ?? null,
    contra: normalizeContra(w.contra),
    origin: normalizeAllocationOrigin(w.origin),
    actor: w.actor ?? null,
    meetingOccurrenceId: w.meetingOccurrenceId ?? null,
    ruleId: w.ruleId ?? null,
    ruleName: w.ruleName ?? null,
  };
}

function normalizePlanned(w: WirePlannedOccurrenceRef): PlannedOccurrenceRef {
  return {
    seriesId: w.seriesId,
    originalOn: w.originalOn,
    payee: w.payee,
    amountMinor: w.amountMinor,
    state: w.state,
  };
}

/** An owner-register line. An unknown `source` reads as `BANK_FILE`. */
export function normalizeOwnerRegisterLine(w: WireOwnerRegisterLine): OwnerRegisterLine {
  const payeeSource = normalizePayeeSource(w.payeeSource);
  const namingSource = normalizeNamingSource(w.namingSource);
  return {
    id: w.id,
    postedOn: w.postedOn,
    amountMinor: w.amountMinor,
    currency: w.currency,
    operatingAmountMinor: w.operatingAmountMinor,
    fxRate: w.fxRate ?? null,
    payee: w.payee,
    payeeRef: w.payeeRef == null ? null : { id: w.payeeRef.id, name: w.payeeRef.name },
    memo: w.memo ?? null,
    runningBalanceMinor: w.runningBalanceMinor ?? null,
    source: SOURCES.has(w.source) ? w.source : 'BANK_FILE',
    status: w.status === 'VOID' ? 'VOID' : 'POSTED',
    allocations: w.allocations.map(normalizeAllocationLine),
    remainderMinor: w.remainderMinor,
    allocatedMinor: w.allocatedMinor,
    afterAllocationsMinor: w.afterAllocationsMinor ?? null,
    plannedOccurrence: w.plannedOccurrence == null ? null : normalizePlanned(w.plannedOccurrence),
    transferAccountId: w.transferAccountId ?? null,
    transferAccountName: w.transferAccountName ?? null,
    transferPairTransactionId: w.transferPairTransactionId ?? null,
    counterpartAmountMinor: w.counterpartAmountMinor ?? null,
    importBatchId: w.importBatchId ?? null,
    alternateName: w.alternateName ?? null,
    ...(w.beforeOpening === true ? { beforeOpening: true as const } : {}),
    ...(payeeSource === null ? {} : { payeeSource }),
    ...(namingSource === null ? {} : { namingSource }),
    ...(w.confirmedByPerson === true ? { confirmedByPerson: true as const } : {}),
    ...(w.wire == null ? {} : { wire: normalizeRegisterWire(w.wire) }),
    ...(w.zelle == null ? {} : { zelle: normalizeRegisterZelle(w.zelle) }),
    ...(w.transfer == null ? {} : { transfer: normalizeRegisterNamedTransfer(w.transfer) }),
  };
}

export function normalizeRegisterNamedTransfer(
  w: WireRegisterNamedTransfer,
): RegisterNamedTransfer {
  return {
    direction: w.direction,
    mask: w.mask != null && w.mask.length > 0 ? w.mask : null,
    counterpartyName: w.counterpartyName ?? null,
    namesOwner: w.namesOwner,
    identityKey: w.identityKey != null && w.identityKey.length > 0 ? w.identityKey : null,
  };
}

export function normalizeRegisterZelle(w: WireRegisterZelle): RegisterZelle {
  return {
    direction: w.direction,
    counterpartyName: w.counterpartyName ?? null,
    namesOwner: w.namesOwner,
    identityKey: w.identityKey ?? null,
  };
}

export function normalizeRegisterWire(w: WireRegisterWire): RegisterWire {
  return {
    direction: w.direction,
    counterpartyName: w.counterpartyName ?? null,
    identifierKind: w.identifierKind ?? null,
    identifier: w.identifier ?? null,
    identifierDisplay: w.identifierDisplay ?? null,
    bank: w.bank ?? null,
    namesOwner: w.namesOwner,
    identityKey: w.identityKey ?? null,
  };
}

function normalizeRegisterTotals(w: WireRegisterTotals): OwnerRegisterTotals {
  return {
    allocatedMinor: w.allocatedMinor,
    count: w.count,
    currency: w.currency,
    inMinor: w.inMinor,
    outMinor: w.outMinor,
    // tmm-api 0145 serves the net; the SPA never adds the flows up (0102 review amendment).
    netMinor: w.netMinor,
    unallocatedMinor: w.unallocatedMinor,
    allCount: w.allCount,
    afterAllocationsMinor: w.afterAllocationsMinor ?? null,
  };
}

/** `{ items, page: { nextCursor }, totals }`. */
export function normalizeOwnerRegisterPage(w: WireOwnerRegisterPage): OwnerRegisterPageResult {
  return {
    items: w.items.map(normalizeOwnerRegisterLine),
    nextCursor: w.page.nextCursor,
    totals: normalizeRegisterTotals(w.totals),
  };
}

/**
 * A register search is 2–100 characters after trim (TMMAPI-0165). Shorter and longer values are
 * not sent: the field keeps what was typed, and the register stays on its ordinary query.
 */
export function sentRegisterQuery(value: string | null | undefined): string {
  const q = value?.trim() ?? '';
  return q.length >= 2 && q.length <= 100 ? q : '';
}

function registerQuery(
  query: OwnerRegisterQuery,
): Record<string, string | number | boolean | undefined> {
  if (query.allocatedOnly === true && query.unallocatedOnly === true) {
    throw new ApiShapeError('allocatedOnly and unallocatedOnly are mutually exclusive');
  }
  const q = sentRegisterQuery(query.q);
  return {
    from: query.from,
    to: query.to,
    unallocatedOnly: query.unallocatedOnly,
    allocatedOnly: query.allocatedOnly,
    cursor: query.cursor,
    limit: query.limit,
    q: q.length > 0 ? q : undefined,
  };
}

export interface AccountSearchLabel {
  id: number;
  name: string;
  last4: string | null;
}

/** One hit of `GET /v1/me/register` (TMMAPI-0165): the owner's line plus its account. */
export interface OwnerWideRegisterLine {
  account: AccountSearchLabel;
  line: OwnerRegisterLine;
}

export interface OwnerWideRegisterPageResult {
  items: OwnerWideRegisterLine[];
  nextCursor: string | null;
  totals: OwnerRegisterTotals | null;
}

export function normalizeOwnerWideRegisterPage(
  w: WireOwnerWideRegisterPage,
): OwnerWideRegisterPageResult {
  return {
    items: w.items.map((item) => ({
      account: { id: item.account.id, name: item.account.name, last4: item.account.last4 ?? null },
      line: normalizeOwnerRegisterLine(item.line),
    })),
    nextCursor: w.page.nextCursor,
    totals: normalizeRegisterTotals(w.totals),
  };
}

export const transactionsApi = {
  /**
   * GET /v1/me/register?q — the same search across every account the caller owns (TMMAPI-0165).
   * `q` is required by the contract (2–100 characters). Anything else is not sent.
   * Subscriber only: `requireSubscriber`.
   */
  async searchAll(query: {
    q: string;
    from?: string;
    to?: string;
    cursor?: string;
    limit?: number;
  }): Promise<OwnerWideRegisterPageResult> {
    const q = sentRegisterQuery(query.q);
    if (q.length === 0) {
      return { items: [], nextCursor: null, totals: null };
    }
    return normalizeOwnerWideRegisterPage(
      await request<WireOwnerWideRegisterPage>('GET', '/v1/me/register', {
        query: {
          q,
          from: query.from,
          to: query.to,
          cursor: query.cursor,
          limit: query.limit,
        },
      }),
    );
  },
  /** GET /v1/me/accounts/{id}/register — omitted `from` / `to` resolve to the server's default. */
  async register(
    accountId: number,
    query: OwnerRegisterQuery = {},
  ): Promise<OwnerRegisterPageResult> {
    return normalizeOwnerRegisterPage(
      await request<WireOwnerRegisterPage>('GET', `/v1/me/accounts/${accountId}/register`, {
        query: registerQuery(query),
      }),
    );
  },
  /** GET /v1/me/accounts/{id}/transactions/{transactionId}. */
  async get(accountId: number, transactionId: number): Promise<OwnerRegisterLine> {
    return normalizeOwnerRegisterLine(
      await request<WireOwnerRegisterLine>(
        'GET',
        `/v1/me/accounts/${accountId}/transactions/${transactionId}`,
      ),
    );
  },
  /** POST /v1/me/accounts/{id}/transactions. An omitted memo is not sent. */
  async createManual(
    accountId: number,
    body: ManualTransactionRequest,
  ): Promise<OwnerRegisterLine> {
    const json: WireManualTransactionRequest = {
      postedOn: body.postedOn,
      amountMinor: body.amountMinor,
      payee: body.payee,
    };
    if (body.memo !== undefined) json.memo = body.memo;
    return normalizeOwnerRegisterLine(
      await request<WireOwnerRegisterLine>('POST', `/v1/me/accounts/${accountId}/transactions`, {
        json,
      }),
    );
  },
  /** POST /v1/me/accounts/{id}/transactions/{transactionId}/void. */
  async void(accountId: number, transactionId: number, body: VoidRequest): Promise<void> {
    await request<void>('POST', `/v1/me/accounts/${accountId}/transactions/${transactionId}/void`, {
      json: body,
    });
  },
};
